import { useState, useEffect, type FunctionComponent, useRef } from "react";
import Draggable from 'react-draggable';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Autocomplete,
    TextField,
    useMediaQuery,
    useTheme,
    Tooltip,
    Paper,
    Backdrop,
    CircularProgress,
    type PaperProps
} from "@mui/material";
import {
    RiCloseLine,
    RiUploadCloudLine,
    RiFileTextLine,
    RiDeleteBin6Line,
    RiFullscreenLine,
    RiFullscreenExitLine,
    RiIdCardLine,
    RiPassportLine,
    RiSteering2Line,
    RiFileListLine
} from "@remixicon/react";

import { obtenerDescripcionTipoDocumento, type PersonalDocumentUser } from "@/shared/types/interfaces/DocumentoPersonalUsuario.interface";
import { TipoDocumentoPersonalEnum } from "@/shared/types/enums/TipoDocumentoPersonal.enum";
import FileViewer from "@/shared/components/FileViewer/FileViewer";
import { FileIconHover } from "../../FileIconHover";
import { fileToBase64 } from "@/modules/login/utils/fileConversion.utils";
import { useUserData } from "@/shared/hooks/useUserData.hook";
import { extraerExtensionArchivo } from "@/modules/GestionParteV2/utils/partesIA.utils";
import { useObtenerDocumentosPersonalesUsuario, useAddPersonalFile, useUpdatePersonalFile } from "@/shared/hooks/useDocumentosPersonales.hook";
import { useTranslation } from 'react-i18next';
import { formatFileSize } from "./helper/formatFileSize";

interface CargarDocumentoPersonalModalProps {
    isOpen: boolean;
    closeModal: () => void;
    onSave: () => void;
    initialData?: PersonalDocumentUser;
}

function DraggablePaperComponent(props: PaperProps) {
    const { isMaximized, ...other } = props as any;
    const nodeRef = useRef(null);
    
    return (
        <Draggable
            nodeRef={nodeRef}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
            disabled={isMaximized}
        >
            <Paper {...other} ref={nodeRef} />
        </Draggable>
    );
}

const DOCUMENT_TYPE_OPTIONS = Object.values(TipoDocumentoPersonalEnum).filter(v => typeof v === 'number') as TipoDocumentoPersonalEnum[];

export const getDocumentIcon = (type?: TipoDocumentoPersonalEnum, props: any = {}) => {
    const iconProps = { ...props };
    if (!type) return <RiFileTextLine {...iconProps} />;
    switch (type) {
        case TipoDocumentoPersonalEnum.CEDULA_IDENTIDAD: return <RiIdCardLine {...iconProps} />;
        case TipoDocumentoPersonalEnum.PASAPORTE: return <RiPassportLine {...iconProps} />;
        case TipoDocumentoPersonalEnum.LICENCIA_CONDUCIR: return <RiSteering2Line {...iconProps} />;
        case TipoDocumentoPersonalEnum.ACTA_NACIMIENTO: return <RiFileListLine {...iconProps} />;
        default: return <RiFileTextLine {...iconProps} />;
    }
};

const MAX_FILE_SIZE_IN_MB = 5; // 5MB
const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_MB * 1024 * 1024;
const ALLOWED_FORMATS = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];

const CargarDocumentoPersonalModal: FunctionComponent<CargarDocumentoPersonalModalProps> = ({
    isOpen,
    closeModal,
    onSave,
    initialData
}) => {
    const { t } = useTranslation('documentosPersonales');
    const [type, setType] = useState<TipoDocumentoPersonalEnum | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<{ type?: string, file?: string }>({});
    const [isDragActive, setIsDragActive] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [viewingFile, setViewingFile] = useState<PersonalDocumentUser | null>(null);
    const [isMaximized, setIsMaximized] = useState(false);
    const { fullName, userData } = useUserData();

    const { data: existingDocs = [], isLoading: isLoadingDocs } = useObtenerDocumentosPersonalesUsuario();

    const addMutation = useAddPersonalFile();
    const updateMutation = useUpdatePersonalFile();

    // Configurar onSuccess para ambas mutaciones
    useEffect(() => {
        if (addMutation.isSuccess) {
            onSave();
            addMutation.reset();
        }
        if (updateMutation.isSuccess) {
            onSave();
            updateMutation.reset();
        }
    }, [addMutation.isSuccess, updateMutation.isSuccess, onSave]);

    const isSaving = addMutation.isPending || updateMutation.isPending;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleView = (doc: PersonalDocumentUser) => {
        setViewingFile(doc);
    };

    useEffect(() => {
        if (initialData) {
            setType(initialData.idType as TipoDocumentoPersonalEnum);
            
        } else {
            setType(null);
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
        setErrors({});
    }, [initialData, isOpen]);

    const getNameFromType = (t: TipoDocumentoPersonalEnum | null) => t ? obtenerDescripcionTipoDocumento(t) : "";

    const handleFileSelect = (selectedFile: File) => {
        if (selectedFile.size > MAX_FILE_SIZE) {
            setErrors(prev => ({ ...prev, file: t('errors.fileTooLarge') }));
            return;
        }
        if (!ALLOWED_FORMATS.includes(selectedFile.type)) {
            setErrors(prev => ({ ...prev, file: t('errors.invalidFormat') }));
            return;
        }
        setFile(selectedFile);
        setErrors(prev => ({ ...prev, file: undefined }));
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async () => {
        const newErrors: typeof errors = {};
        if (!type) newErrors.type = t('errors.typeRequired');
        if (!initialData && !file) newErrors.file = t('errors.fileRequired');

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        

        if (!initialData && existingDocs.length > 0) {
            if (existingDocs.some(d => d.idType === type)) {
                setErrors(prev => ({ ...prev, type: t('errors.duplicateType') }));
                return;
            }
        }

        const docName = getNameFromType(type);

        const base64 = await fileToBase64(file!, true);
        const extension = extraerExtensionArchivo(file!.name);

        if (initialData) {
            updateMutation.mutate({
                idPersonalDocumentUser: initialData.idPersonalDocumentUser,
                updates: {
                    NombreDocumento: `${docName}${fullName.replace(' ', '_')}.${extension}`,
                    IdTipo: type!,
                    Base64: base64
                }
            });
        } else {
            if (file) {
                addMutation.mutate({
                    NombreDocumento: `${docName}${fullName.replace(' ', '_')}.${extension}`,
                    IdTipo: type!,
                    Base64: base64,
                });
            }
        }

    };

    const handlePreview = (/*e: React.MouseEvent*/) => {
        // e.stopPropagation();
        if (file) {
             const url = URL.createObjectURL(file);
             setPreviewUrl(url);
             setIsPreviewOpen(true);
         } else if (initialData?.fileUrl) {
             setPreviewUrl(initialData.fileUrl);
             setIsPreviewOpen(true);
         }
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        if (file && previewUrl && previewUrl.startsWith('blob:')) {
             URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
    };

    const handleRemoveFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };
const handleToggleMaximize = () => {
        setIsMaximized(!isMaximized);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={closeModal}
            maxWidth={isMaximized ? false : "sm"}
            fullWidth
            fullScreen={isMaximized || isMobile}
            PaperComponent={DraggablePaperComponent}
            PaperProps={{
                isMaximized: isMaximized || isMobile,
                sx: { 
                    borderRadius: isMaximized ? 0 : 2,
                    margin: isMaximized ? 0 : undefined,
                    width: isMaximized ? '100vw' : undefined,
                    height: isMaximized ? '100vh' : 'auto',
                    maxHeight: isMaximized ? '100vh' : 'calc(100% - 64px)',
                    maxWidth: isMaximized ? '100vw' : undefined,
                    transform: isMaximized ? 'none !important' : undefined
                }
            } as any}
        >
            <DialogTitle 
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0',
                    pb: 2,
                    cursor: isMaximized ? 'default' : 'move'
                }}
                id="draggable-dialog-title"
            >
                <Box display="flex" alignItems="center" gap={1}>
                    {getDocumentIcon(type || initialData?.idType, { size: 24 })}
                    <Typography variant="h6" fontWeight="600" color="text.primary" noWrap>
                        {initialData ? t('upload.editTitle') : t('upload.newTitle')}
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <IconButton onClick={handleToggleMaximize} size="small">
                        {isMaximized ? <RiFullscreenExitLine size={24} /> : <RiFullscreenLine size={24} />}
                    </IconButton>
                    <IconButton onClick={closeModal} size="small">
                        <RiCloseLine size={24} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column' }}>
                <div className="flex flex-col gap-6 mt-2 flex-grow">
                    <Autocomplete
                        options={DOCUMENT_TYPE_OPTIONS}
                        getOptionLabel={(option) => obtenerDescripcionTipoDocumento(option)}
                        value={type}
                        disabled={!!initialData}
                        onChange={(_, newValue) => {
                            setType(newValue);
                            setErrors(prev => ({ ...prev, type: undefined }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('form.typeLabel')}
                                placeholder={t('form.typePlaceholder')}
                                error={!!errors.type}
                                helperText={errors.type}
                                fullWidth
                            />
                        )}
                    />

                    {/* Upload Zone */}
                    <div className="w-full flex-grow flex flex-col">
                        <Box
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                border: '2px dashed',
                                borderColor: errors.file ? '#d32f2f' : isDragActive ? 'primary.main' : 'grey.300',
                                borderRadius: 2,
                                p: 3,
                                textAlign: 'center',
                                backgroundColor: isDragActive ? 'action.hover' : 'grey.50',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: isMaximized ? '300px' : 'auto',
                                '&:hover': {
                                    borderColor: 'primary.main',
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        >
                            <input
                                type="file"
                                hidden
                                ref={fileInputRef}
                                accept=".pdf, .png, .jpg, .jpeg"
                                onChange={(e) => {
                                    if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
                                }}
                            />

                            {!file && !initialData?.fileName ? (
                                <div className="flex flex-col items-center gap-2">
                                    <div className="p-3 bg-blue-50 rounded-full text-blue-600">
                                        <RiUploadCloudLine size={32} />
                                    </div>
                                    <Typography variant="subtitle1" fontWeight="500" color="text.primary">
                                        {isMobile ? t('upload.touchOrTakePhoto') : t('upload.clickOrDrag')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('upload.allowed', { maxMB: MAX_FILE_SIZE_IN_MB })}
                                    </Typography>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between w-full bg-white p-3 rounded border border-gray-200 shadow-sm">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="p-2 bg-red-50 text-red-500 rounded">
                                            {getDocumentIcon((type || initialData?.idType) as TipoDocumentoPersonalEnum, { size: 24 })}
                                        </div>
                                        <div className="flex flex-col items-start overflow-hidden">
                                            <Typography variant="body2" fontWeight="500" noWrap className="max-w-[200px]">
                                                {file ? file.name : initialData?.fileName}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {file ? formatFileSize(file.size) : t('upload.fileSaved')}
                                            </Typography>
                                        </div>
                                    </div>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <FileIconHover
                                                fileName={file ? file.name : (initialData?.fileName || "")}
                                                onView={handlePreview}
                                                size="xs"
                                            />
                                        </div>
                                        <Tooltip title={t('upload.removeFileTooltip')} arrow>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={handleRemoveFile}
                                            >
                                                <RiDeleteBin6Line size={20} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </div>
                            )}
                        </Box>
                        {errors.file && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                {errors.file}
                            </Typography>
                        )}
                    </div>
                </div>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
                <Button
                    onClick={closeModal}
                    variant="outlined"
                    color="inherit"
                    sx={{ textTransform: 'none', px: 3 }}
                >
                    {t('upload.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disableElevation
                    disabled={isSaving}
                    sx={{ textTransform: 'none', px: 3 }}
                >
                    {isSaving ? t('upload.saving') : t('upload.save')}
                </Button>
            </DialogActions>

            {isPreviewOpen && previewUrl && (
                <FileViewer
                    fileUrl={previewUrl}
                    fileName={file ? file.name : (initialData?.fileName || "Documento")}
                    autoOpen={true}
                    onClose={handleClosePreview}
                />
            )}

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isSaving}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Dialog>
    );
};

export default CargarDocumentoPersonalModal;
